import { BreadcrumbItem, NavItem } from '@app/types';
import { LayoutGrid, Database } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { AdminPermissions } from '@modules/auth/permissions/admin.permission.shared';

type RegistryItem = NavItem & { parent?: string };

const pluginNavItems: NavItem[] = [];
const pendingChildren: Record<string, RegistryItem[]> = {};
const listeners = new Set<() => void>();
let revision = 0;

const notify = () => {
    revision += 1;
    listeners.forEach((fn) => fn());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getSnapshot = () => revision;

export const structureRegistry = {
    /**
     * Register a sidebar item.
     * Optional `parent` is a path like "dashboard/survey". If the parent doesn't
     * exist yet, the item is queued and resolved on the next snapshot.
     */
    registerSidebarItem(item: RegistryItem) {
        const parentRaw = item.parent?.replace(/^\/|\/$/g, '');
        if (!parentRaw) {
            pluginNavItems.push(item);
        } else {
            pendingChildren[parentRaw] = pendingChildren[parentRaw] ?? [];
            pendingChildren[parentRaw].push(item);
        }
        notify();
    },
};

const MAIN_NAV_TEMPLATE: NavItem[] = [
    { id: 'dashboard', title: 'pages.dashboard.title', url: '/dashboard', icon: LayoutGrid, order: -1000 },
    { id: 'database', title: 'admin.database', url: '/admin/database', icon: Database, order: 900, permissions: AdminPermissions.database },
];

const FOOTER_NAV_TEMPLATE: NavItem[] = [];

const cloneNav = (items: NavItem[]): NavItem[] =>
    items.map((i) => ({
        ...i,
        items: i.items ? cloneNav(i.items) : undefined,
    }));

const findById = (lists: NavItem[][], id: string): NavItem | undefined => {
    for (const list of lists) {
        const found = list.find((n) => n.id === id);
        if (found) return found;
    }
    return undefined;
};

const resolvePathToNode = (
    mainList: NavItem[],
    pluginList: NavItem[],
    pathParts: string[],
): NavItem | undefined => {
    let parentNode: NavItem | undefined;
    for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        parentNode = i === 0
            ? findById([mainList, pluginList], part)
            : (parentNode?.items ?? []).find((n) => n.id === part);
        if (!parentNode) return undefined;
    }
    return parentNode;
};

const attachPending = (mainList: NavItem[], pluginList: NavItem[]) => {
    for (const parentPath of Object.keys(pendingChildren)) {
        const children = pendingChildren[parentPath];
        const parts = parentPath.split('/').filter(Boolean);
        const parentNode = resolvePathToNode(mainList, pluginList, parts);
        if (!parentNode) {
            console.warn(`Sidebar children are waiting for unknown parent path: ${parentPath}.`);
            continue;
        }
        parentNode.items = parentNode.items ?? [];
        for (const child of children) {
            const existing = parentNode.items.find((i) => i.id === child.id);
            if (existing) {
                Object.assign(existing, child);
            } else {
                const { parent: _parent, ...rest } = child;
                parentNode.items.push(rest as NavItem);
            }
        }
    }
};

const buildStructure = () => {
    const main = cloneNav(MAIN_NAV_TEMPLATE);
    const plugin = cloneNav(pluginNavItems);
    const footer = cloneNav(FOOTER_NAV_TEMPLATE);
    attachPending(main, plugin);
    return { main, plugin, footer };
};

const addTranslations = <T extends NavItem | BreadcrumbItem>(
    t: ReturnType<typeof useTranslation>['t'],
    navItems: T[],
): T[] =>
    navItems.map<T>((i) => ({
        ...i,
        title: (() => {
            const translated = t(i.title);
            return typeof translated === 'string' ? translated : i.title;
        })(),
        ...('items' in i && { items: i.items ? addTranslations<T>(t, i.items as T[]) : undefined }),
    } as T));

const useStructure = () => {
    const { t } = useTranslation();
    useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    const { main, plugin, footer } = useMemo(() => buildStructure(), [revision]);

    const getNavItemById = useCallback(
        (id: string) => {
            const fallback: NavItem[] = [
                { id: 'edit', title: 'common.buttons.edit' },
                { id: 'create', title: 'common.buttons.create' },
            ];
            const list: NavItem[] = [...main, ...plugin, ...footer, ...fallback];
            const findItem = (items: NavItem[]): NavItem | undefined => {
                for (const item of items) {
                    if (item.id === id) return item;
                    const child = item.items ? findItem(item.items) : undefined;
                    if (child) return child;
                }
                return undefined;
            };
            return findItem(list);
        },
        [main, plugin, footer],
    );

    const buildBreadcrumb = useCallback(
        (...ids: (string | BreadcrumbItem)[]) =>
            addTranslations(
                i18next.t,
                ids.map((id): BreadcrumbItem => {
                    if (typeof id !== 'string') return id;
                    const navItem = getNavItemById(id);
                    return {
                        title: navItem?.title ?? id,
                        href: navItem?.url ?? '',
                    };
                }),
            ),
        [getNavItemById],
    );

    return useMemo(
        () => ({
            mainNavItems: addTranslations(t, [...main, ...plugin]).sort(
                (a, b) => (a.order ?? 0) - (b.order ?? 0),
            ),
            footerNavItems: footer,
            buildBreadcrumb,
            getNavItemById,
        }),
        [t, main, plugin, footer, buildBreadcrumb, getNavItemById],
    );
};

export default useStructure;
